import { GearIcon, LightningIcon, PlusIcon, UploadSimpleIcon } from '@phosphor-icons/react'
import { ColumnDef, ColumnHelper, createColumnHelper, Row } from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Routes } from '../../routes'
import {
  useGetLatestAnalysisForRepository,
  useGetRepositoryIntegrations,
  useGetWorkflows,
} from '../../utils/api-client/api-hooks'
import type { Repository, Tool } from '../../utils/api-client/api-schemas'
import { convertIntegrationTypeToTool } from '../../utils/repository-utils.tsx'
import type { DrawerContent } from '../drawers/workflow-drawer'

import classNames from 'classnames'
import { LatestAnalysisLink } from '../latest-analysis-link'
import { SyncLatestDropdown } from '../menus/sync-latest-dropdown'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { TooltipWithHover } from '../tooltip'
import * as styles from './repositories-table.css'

/**
 * The share reserved for the single extra column a route may splice in — today the risk column.
 *
 * The four widths have to sum to 100%, and they were declared in two files and re-asserted in a third, so
 * tuning one meant knowing to go and rebalance the others. This is the one number both sides derive from.
 * It assumes **exactly one** extra column: a second would overflow silently.
 */
export const EXTRA_COLUMN_WIDTH_PERCENT = 34

export type UseRepositoriesTableColumnDefinitionsOptions = Omit<
  Parameters<typeof createRepositoryColumnDefinition>[0] &
    Parameters<typeof createAnalysisColumnDefinition>[0] &
    Parameters<typeof createActionsColumnDefinition>[0],
  'columnHelper'
>

export const useRepositoriesTableColumnDefinitions = ({
  setSelectedWorkflowRepositoryId,
  setSelectedSettingsRepositoryId,
  setWorkflowDrawerContent,
  onAddFindings,
  onSyncTool,
  hasExtraColumns = false,
}: UseRepositoriesTableColumnDefinitionsOptions) => {
  return useMemo(() => {
    const columnHelper = createColumnHelper<Repository>()

    return [
      createRepositoryColumnDefinition({
        columnHelper,
        setSelectedWorkflowRepositoryId,
        setWorkflowDrawerContent,
        hasExtraColumns,
      }),
      createAnalysisColumnDefinition({ columnHelper, onAddFindings, hasExtraColumns }),
      createActionsColumnDefinition({
        columnHelper,
        setSelectedWorkflowRepositoryId,
        setSelectedSettingsRepositoryId,
        setWorkflowDrawerContent,
        onAddFindings,
        onSyncTool,
        hasExtraColumns,
      }),
    ] satisfies ColumnDef<Repository>[]
  }, [
    setSelectedWorkflowRepositoryId,
    setSelectedSettingsRepositoryId,
    setWorkflowDrawerContent,
    onAddFindings,
    onSyncTool,
    hasExtraColumns,
  ])
}

const RepositoryInfo: React.FC<{ repository: Pick<Repository, 'type' | 'full_name'> }> = ({ repository }) => {
  return (
    <span className={styles.repositoryName}>
      <div style={{ marginRight: '12px' }}>
        <SCMIcon type={repository.type} size="medium" />
      </div>
      {repository.full_name}
    </span>
  )
}

// #region Repository column

const createRepositoryColumnDefinition = ({
  columnHelper,
  setSelectedWorkflowRepositoryId,
  setWorkflowDrawerContent,
  hasExtraColumns,
}: {
  columnHelper: ColumnHelper<Repository>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  /**
   * Whether a further column shares the row — today the repositories page's risk column.
   *
   * Named for the column count rather than for risk: `architecture.test.ts` forbids a component importing
   * from `routes/`, and a flag called `hasRiskColumn` would carry a route's concept into a shared table.
   *
   * **Both cases declare widths.** `tableLayout: 'fixed'` is unconditional, and under it a column with no
   * width does not size to content — the browser splits the space equally. Measured: the three-column
   * state came out as three 364px thirds, giving Actions as much room as the repository name. Each case
   * sums to 100%; the four-column one reserves `EXTRA_COLUMN_WIDTH_PERCENT` for the spliced column.
   */
  hasExtraColumns?: boolean
}) => {
  return columnHelper.accessor(row => row.full_name, {
    id: 'repository',
    // Bounded rather than left to absorb the slack *when a fourth column shares the row*: unconstrained it
    // pushed the scan column far right, leaving a gulf between a repository's name and the rest of its row.
    // Not narrower than this — below ~31% typical `owner/name` pairs wrap to two lines at 1120px.
    meta: hasExtraColumns ? { width: '31%' } : { width: '55%' },
    cell: ({ row }) => (
      <RepositoryColumnCell
        row={row}
        setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
        setWorkflowDrawerContent={setWorkflowDrawerContent}
      />
    ),
    header: RepositoryColumnHeader,
  }) as ColumnDef<Repository>
}

const RepositoryColumnHeader: React.FC = () => <span>REPOSITORY</span>

const RepositoryColumnCell: React.FC<{
  row: Row<Repository>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
}> = ({ row, setSelectedWorkflowRepositoryId, setWorkflowDrawerContent }) => {
  const { data: workflowsData } = useGetWorkflows({ repositoryId: row.original.id })
  const workflowCount = workflowsData?.total ?? 0

  const handleLightningClick = () => {
    setSelectedWorkflowRepositoryId(row.original.id)
    if (setWorkflowDrawerContent) {
      setWorkflowDrawerContent('manage')
    }
  }

  return (
    <div className={styles.repositoryNameContainer}>
      <Link
        to={Routes.RepositoryDetailsPage.createPath({ repositoryId: row.original.id })}
        className={styles.repositoryNameLink}
      >
        <RepositoryInfo repository={row.original} />
      </Link>
      {workflowCount > 0 && (
        <TooltipWithHover
          trigger={
            <button className={styles.workflowButton} aria-label="Workflows" onClick={handleLightningClick}>
              <LightningIcon size={16} weight="fill" className={styles.workflowIndicator} />
            </button>
          }
          size="small"
        >
          <span>
            Manage {workflowCount} {workflowCount === 1 ? 'workflow' : 'workflows'}
          </span>
        </TooltipWithHover>
      )}
    </div>
  )
}
// #endregion Repository column

// #region Analysis column

type CreateAnalysisColumnDefinitionOptions = {
  columnHelper: ColumnHelper<Repository>
  onAddFindings: (repositoryId: string) => unknown
  /** See {@link createRepositoryColumnDefinition} — widths apply only when another column shares the row. */
  hasExtraColumns?: boolean
}

const createAnalysisColumnDefinition = ({
  columnHelper,
  onAddFindings,
  hasExtraColumns,
}: CreateAnalysisColumnDefinitionOptions) =>
  columnHelper.display({
    id: 'analysis',
    // At 17% a longer tool name wrapped, and the column sat hard against Actions with the whole group
    // pushed to the right edge.
    meta: hasExtraColumns ? { width: '24%' } : { width: '30%' },
    cell: AnalysisColumnCell({ onAddFindings }),
    header: AnalysisColumnHeader,
  })

const AnalysisColumnHeader: React.FC = () => <span className={styles.center}>LATEST SCAN WITH ANALYSIS</span>

const AnalysisColumnCell: ({
  onAddFindings,
}: Pick<CreateAnalysisColumnDefinitionOptions, 'onAddFindings'>) => React.FC<{
  row: Row<Repository>
}> =
  ({ onAddFindings }) =>
  ({ row }) => {
    const { data: latestAnalysis } = useGetLatestAnalysisForRepository({ repositoryId: row.original.id })

    return (
      <div className={styles.center}>
        {latestAnalysis ? (
          <LatestAnalysisLink latestAnalysis={latestAnalysis} />
        ) : (
          <a
            href="#"
            className={styles.uploadScanLink}
            onClick={e => {
              e.preventDefault()
              onAddFindings(row.original.id)
            }}
          >
            Add findings
            <UploadSimpleIcon size={15} className={styles.uploadIconLatestAnalysis} />
          </a>
        )}
      </div>
    )
  }

// #endregion Analysis column

// #region Actions column

type CreateActionsColumnDefinitionOptions = ActionsColumnCellFactoryOptions & {
  columnHelper: ColumnHelper<Repository>
  /** See {@link createRepositoryColumnDefinition} — widths apply only when another column shares the row. */
  hasExtraColumns?: boolean
}

const createActionsColumnDefinition = ({
  columnHelper,
  setSelectedWorkflowRepositoryId,
  setSelectedSettingsRepositoryId,
  setWorkflowDrawerContent,
  onAddFindings,
  onSyncTool,
  hasExtraColumns,
}: CreateActionsColumnDefinitionOptions) =>
  columnHelper.display({
    id: 'actions',
    // A share rather than a fixed 110px, so the four columns divide the table between them instead of
    // Actions pinning a slab on the right that the scan column then presses up against. The smallest
    // share of the four: the cell holds icon buttons that only appear on row hover.
    meta: hasExtraColumns ? { width: '11%' } : { width: '15%' },
    cell: ActionsColumnCell({
      setSelectedWorkflowRepositoryId,
      setSelectedSettingsRepositoryId,
      setWorkflowDrawerContent,
      onAddFindings,
      onSyncTool,
    }),
    header: ActionsColumnHeader,
  })

const ActionsColumnHeader: React.FC = () => <span>Actions</span>

type ActionsColumnCellFactoryOptions = {
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setSelectedSettingsRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  onAddFindings: (repositoryId: string) => unknown
  onSyncTool: (repositoryId: string, tool: Tool, repositoryName: string) => unknown
}

const ActionsColumnCell: (options: ActionsColumnCellFactoryOptions) => React.FC<{ row: Row<Repository> }> =
  ({
    setSelectedWorkflowRepositoryId,
    setSelectedSettingsRepositoryId,
    setWorkflowDrawerContent,
    onAddFindings,
    onSyncTool,
  }) =>
  ({ row }) => {
    const [isSyncDropdownOpen, setIsSyncDropdownOpen] = useState(false)
    const { data: integrations } = useGetRepositoryIntegrations({ repositoryId: row.original.id })
    const mappedTools: Tool[] = useMemo(
      () => [
        ...new Set(
          (integrations?._embedded?.items ?? []).map(integration => convertIntegrationTypeToTool(integration.type))
        ),
      ],
      [integrations]
    )

    return (
      <div
        className={classNames(styles.center, styles.actionsContainer)}
        data-dropdown-open={isSyncDropdownOpen || undefined}
      >
        <AddFindingsActionButton onAddFindings={onAddFindings} row={row} />
        <SyncLatestDropdown
          repositoryId={row.original.id}
          mappedTools={mappedTools}
          onSyncTool={tool => onSyncTool(row.original.id, tool, row.original.name)}
          triggerClassName={styles.actionButton}
          onOpenChange={setIsSyncDropdownOpen}
        />
        <ManageWorkflowsActionButton
          setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
          setWorkflowDrawerContent={setWorkflowDrawerContent}
          row={row}
        />
        <RepositorySettingsActionButton setSelectedSettingsRepositoryId={setSelectedSettingsRepositoryId} row={row} />
      </div>
    )
  }

const AddFindingsActionButton: React.FC<{
  row: Row<Repository>
  onAddFindings: (repositoryId: string) => unknown
}> = ({ onAddFindings, row }) => {
  return (
    <TooltipWithHover
      trigger={
        <button
          className={styles.actionButton}
          onClick={() => onAddFindings(row.original.id)}
          aria-label="Add findings for analysis"
        >
          <PlusIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Add findings for analysis</span>
    </TooltipWithHover>
  )
}

const ManageWorkflowsActionButton: React.FC<{
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  row: Row<Repository>
}> = ({ setSelectedWorkflowRepositoryId, setWorkflowDrawerContent, row }) => {
  const handleClick = () => {
    setSelectedWorkflowRepositoryId(row.original.id)
    if (setWorkflowDrawerContent) {
      setWorkflowDrawerContent('create')
    }
  }
  return (
    <TooltipWithHover
      trigger={
        <button className={styles.actionButton} onClick={handleClick} aria-label="Create workflow">
          <LightningIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Create workflow</span>
    </TooltipWithHover>
  )
}

const RepositorySettingsActionButton: React.FC<{
  setSelectedSettingsRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  row: Row<Repository>
}> = ({ setSelectedSettingsRepositoryId, row }) => {
  return (
    <TooltipWithHover
      trigger={
        <button
          className={styles.actionButton}
          onClick={() => setSelectedSettingsRepositoryId(row.original.id)}
          aria-label="Repository settings"
        >
          <GearIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Repository settings</span>
    </TooltipWithHover>
  )
}

// #endregion Actions column
